import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import MobileMenuModal from './MobileMenuModal';
import CategoriesMegaMenu from './CategoriesMegaMenu';
import AuthModal from './AuthModal';
import { getNotifications, getUnreadNotificationCount, markNotificationAsRead, markAllNotificationsAsRead } from '../services/api';

function Header({ logo, appName, cartCount = 0, onCartClick, cartButtonRef, searchQuery = '', onSearchChange, activeFilter = null, onFilterChange, onCategoryFilterChange, user, onLoginSuccess, onLogout, authModalOpen: controlledAuthModalOpen, setAuthModalOpen: controlledSetAuthModalOpen, redirectAfterLogin = false, redirectAfterRegister = false }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categoriesMenuOpen, setCategoriesMenuOpen] = useState(false);
  const [internalAuthModalOpen, setInternalAuthModalOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const [desktopUserMenuOpen, setDesktopUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications] = useState(false);
  const notificationsButtonRef = useRef(null);
  const notificationsIntervalRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const previousUnreadCountRef = useRef(-1); // Use -1 as initial state to distinguish from 0
  
  // Use controlled state if provided, otherwise use internal state
  const authModalOpen = controlledAuthModalOpen !== undefined ? controlledAuthModalOpen : internalAuthModalOpen;
  const setAuthModalOpen = controlledSetAuthModalOpen || setInternalAuthModalOpen;
  const searchContainerRef = useRef(null);
  const searchIconRef = useRef(null);
  const categoriesButtonRef = useRef(null);
  const categoriesMenuTimeoutRef = useRef(null);
  const userMenuButtonRef = useRef(null);
  const mobileUserMenuButtonRef = useRef(null);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Check if user is admin
  const isAdmin = user?.role?.name === 'super_admin' || user?.role?.name === 'administrator' || user?.role?.name === 'manager';
  // Check if user is courier
  const isCourier = user?.role?.name === 'courier';

  // Debug: Log user profile picture on mount and updates
  useEffect(() => {
    if (user) {
      console.log('Header - User object:', { 
        id: user.id, 
        name: user.name, 
        profile_picture: user.profile_picture,
        hasProfilePicture: !!user.profile_picture 
      });
    }
  }, [user]);

  // Sync local search query with prop
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Initialize notification sound
  useEffect(() => {
    // Create a longer, more noticeable notification sound using Web Audio API
    // This creates a pleasant two-tone chime sound without requiring an external file
    const playNotificationSound = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume AudioContext if suspended (required by some browsers)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        const duration = 0.8; // 800ms - longer and more noticeable
        const now = audioContext.currentTime;
        
        // Create two oscillators for a chime effect (two-tone notification)
        const oscillator1 = audioContext.createOscillator();
        const oscillator2 = audioContext.createOscillator();
        const gainNode1 = audioContext.createGain();
        const gainNode2 = audioContext.createGain();
        const masterGain = audioContext.createGain();
        
        // Connect oscillators to their gain nodes
        oscillator1.connect(gainNode1);
        oscillator2.connect(gainNode2);
        gainNode1.connect(masterGain);
        gainNode2.connect(masterGain);
        masterGain.connect(audioContext.destination);
        
        // First tone: 880Hz (A5 note) - more musical
        oscillator1.frequency.value = 880;
        oscillator1.type = 'sine';
        
        // Second tone: 1320Hz (E6 note) - harmonic interval
        oscillator2.frequency.value = 1320;
        oscillator2.type = 'sine';
        
        // Set volume envelopes for both tones
        // First tone: starts immediately, fades out
        gainNode1.gain.setValueAtTime(0.4, now);
        gainNode1.gain.exponentialRampToValueAtTime(0.01, now + duration * 0.6);
        
        // Second tone: starts slightly delayed for chime effect
        gainNode2.gain.setValueAtTime(0, now);
        gainNode2.gain.setValueAtTime(0.4, now + 0.15); // Starts after 150ms
        gainNode2.gain.exponentialRampToValueAtTime(0.01, now + duration);
        
        // Master volume
        masterGain.gain.setValueAtTime(0.5, now);
        
        // Play the sound
        oscillator1.start(now);
        oscillator1.stop(now + duration);
        oscillator2.start(now);
        oscillator2.stop(now + duration);
      } catch (error) {
        // Fallback: try HTML5 Audio API if Web Audio API fails
        try {
          // Create a more noticeable beep with multiple tones using data URI
          // This is a simple two-tone chime encoded as WAV
          const beepSound = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQCEah4fLDbiQFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQCEah4fLDbiQFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURAJRqTg8sFuJAUwgM/z1oY5CCBrwO/knlMQ");
          beepSound.volume = 0.5;
          beepSound.play().catch(() => {
            // Ignore errors if autoplay is blocked
          });
        } catch (fallbackError) {
          console.warn('Could not play notification sound:', fallbackError);
        }
      }
    };
    
    notificationSoundRef.current = playNotificationSound;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (categoriesMenuTimeoutRef.current) {
        clearTimeout(categoriesMenuTimeoutRef.current);
      }
      if (notificationsIntervalRef.current) {
        clearInterval(notificationsIntervalRef.current);
      }
    };
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const data = await getNotifications(token);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [user]);

  // Fetch unread count only (lighter request)
  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      previousUnreadCountRef.current = -1;
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const data = await getUnreadNotificationCount(token);
      const newCount = data.unread_count || 0;
      const previousCount = previousUnreadCountRef.current;
      
      // Play sound if count increased (new notification received)
      // Only play if there was a previous count >= 0 to avoid playing on initial load
      // Also play if count goes from 0 to any positive number (first notification)
      if (newCount > previousCount && previousCount >= 0 && notificationSoundRef.current) {
        // Play sound immediately when notification arrives (don't await, fire and forget)
        notificationSoundRef.current().catch((error) => {
          console.warn('Failed to play notification sound:', error);
        });
      }
      
      previousUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [user]);

  // Initial fetch and set up polling
  useEffect(() => {
    if (user) {
      fetchNotifications().then(() => {
        // Set initial unread count after first fetch
        const token = localStorage.getItem('auth_token');
        if (token) {
          getUnreadNotificationCount(token).then(data => {
            previousUnreadCountRef.current = data.unread_count || 0;
            setUnreadCount(data.unread_count || 0);
          }).catch(() => {});
        }
      });
      
      // Poll for unread count every 2 seconds for near real-time notifications
      notificationsIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, 2000);

      // Listen for custom events to trigger immediate notification refresh
      const handleNotificationRefresh = () => {
        fetchUnreadCount();
        if (notificationsOpen) {
          fetchNotifications();
        }
      };

      window.addEventListener('notification-refresh', handleNotificationRefresh);

      return () => {
        if (notificationsIntervalRef.current) {
          clearInterval(notificationsIntervalRef.current);
        }
        window.removeEventListener('notification-refresh', handleNotificationRefresh);
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
      previousUnreadCountRef.current = -1;
    }
  }, [user, fetchNotifications, fetchUnreadCount, notificationsOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (notificationsOpen && user) {
      fetchNotifications().then(() => {
        // Update previous count when opening dropdown to prevent sound on close
        const token = localStorage.getItem('auth_token');
        if (token) {
          getUnreadNotificationCount(token).then(data => {
            previousUnreadCountRef.current = data.unread_count || 0;
          }).catch(() => {});
        }
      });
    }
  }, [notificationsOpen, user, fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Mark as read if unread and remove from list
      if (!notification.read_at) {
        await markNotificationAsRead(notification.id, token);
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Remove notification from list immediately
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } else {
        // Even if already read, remove it when clicked
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }

      // Navigate to support ticket
      if (notification.ticket_id) {
        const isAdmin = user?.role?.name === 'super_admin' || user?.role?.name === 'administrator' || user?.role?.name === 'manager';
        navigate(isAdmin ? '/admin/support' : '/support');
        setNotificationsOpen(false);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      await markAllNotificationsAsRead(token);
      setUnreadCount(0);
      // Remove all notifications from the list when marked as read
      setNotifications([]);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleSearchChange = (value) => {
    setLocalSearchQuery(value);
    // Update search in real-time as user types
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Ensure search is applied on form submit (mobile)
    if (onSearchChange) {
      onSearchChange(localSearchQuery);
    }
    // Close mobile search after submission
    if (mobileSearchOpen) {
      setMobileSearchOpen(false);
    }
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileSearchOpen && 
          searchContainerRef.current && 
          !searchContainerRef.current.contains(event.target) &&
          searchIconRef.current &&
          !searchIconRef.current.contains(event.target)) {
        setMobileSearchOpen(false);
      }
    };

    if (mobileSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [mobileSearchOpen]);

  return (
    <header className="fixed top-[36px] sm:top-[40px] left-0 right-0 bg-white border-b border-gray-200 py-2 sm:py-3 z-50 shadow-sm w-full max-w-full overflow-x-hidden">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between relative w-full">
          {/* Logo */}
          <div className="flex-shrink-0 pr-2 sm:pr-4 lg:pr-6">
            <Link to="/">
              <img src={logo} alt={appName} className="h-8 sm:h-10 lg:h-12 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 flex-shrink-0">
            <button
              onClick={() => {
                if (onFilterChange) {
                  onFilterChange(activeFilter === 'best-selling' ? null : 'best-selling');
                }
                if (onSearchChange) {
                  onSearchChange(''); // Clear search when filtering
                }
              }}
              className={`text-secondary no-underline font-semibold text-[15px] px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 ${
                activeFilter === 'best-selling'
                  ? 'text-primary bg-primary/10 font-bold'
                  : 'hover:text-primary hover:bg-primary/10 hover:font-bold'
              }`}
            >
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
              </svg>
              Best-Selling Items
            </button>
            <button
              onClick={() => {
                if (onFilterChange) {
                  onFilterChange(activeFilter === '5-star' ? null : '5-star');
                }
                if (onSearchChange) {
                  onSearchChange(''); // Clear search when filtering
                }
              }}
              className={`text-secondary no-underline font-semibold text-[15px] px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 ${
                activeFilter === '5-star'
                  ? 'text-primary bg-primary/10 font-bold'
                  : 'hover:text-primary hover:bg-primary/10 hover:font-bold'
              }`}
            >
              <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              5-Star Rated
            </button>
            <div 
              className="relative"
              ref={categoriesButtonRef}
              onMouseEnter={() => {
                // Clear any pending close timeout
                if (categoriesMenuTimeoutRef.current) {
                  clearTimeout(categoriesMenuTimeoutRef.current);
                  categoriesMenuTimeoutRef.current = null;
                }
                setCategoriesMenuOpen(true);
              }}
              onMouseLeave={(e) => {
                // Clear any pending timeout
                if (categoriesMenuTimeoutRef.current) {
                  clearTimeout(categoriesMenuTimeoutRef.current);
                }
                // Check if mouse is moving to the mega menu
                const isMovingToMenu = e.relatedTarget && (
                  e.relatedTarget.closest('.categories-mega-menu') ||
                  e.relatedTarget.closest('[class*="categories-mega-menu"]')
                );
                // Only close if not moving to the mega menu, with a delay
                if (!isMovingToMenu) {
                  categoriesMenuTimeoutRef.current = setTimeout(() => {
                    setCategoriesMenuOpen(false);
                    categoriesMenuTimeoutRef.current = null;
                  }, 300); // 300ms delay before closing
                }
              }}
            >
              <button
                className={`text-secondary no-underline font-semibold text-[15px] px-3 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 ${
                  categoriesMenuOpen || (activeFilter && activeFilter.startsWith('category-'))
                    ? 'text-primary bg-primary/10 font-bold'
                    : 'hover:text-primary hover:bg-primary/10 hover:font-bold'
                }`}
              >
                <svg className="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Categories
                <svg className={`w-3.5 h-3.5 text-secondary transition-transform duration-200 ${categoriesMenuOpen ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div 
              className="categories-mega-menu"
              onMouseEnter={() => {
                // Clear any pending close timeout
                if (categoriesMenuTimeoutRef.current) {
                  clearTimeout(categoriesMenuTimeoutRef.current);
                  categoriesMenuTimeoutRef.current = null;
                }
                setCategoriesMenuOpen(true);
              }}
              onMouseLeave={() => {
                // Add delay before closing
                categoriesMenuTimeoutRef.current = setTimeout(() => {
                  setCategoriesMenuOpen(false);
                  categoriesMenuTimeoutRef.current = null;
                }, 300); // 300ms delay before closing
              }}
            >
              <CategoriesMegaMenu 
                isOpen={categoriesMenuOpen} 
                onClose={() => setCategoriesMenuOpen(false)}
                onCategoryFilterChange={onCategoryFilterChange}
                categoriesButtonRef={categoriesButtonRef}
              />
            </div>
          </nav>

          {/* Search Bar */}
          <div className="hidden sm:flex flex-1 max-w-3xl mx-2 lg:mx-4">
            <form className="relative w-full" onSubmit={handleSearchSubmit}>
              <input 
                type="text" 
                value={localSearchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-4 sm:pl-5 pr-10 sm:pr-12 py-1.5 sm:py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary bg-gray-50/50 text-xs sm:text-sm transition-all duration-200 hover:bg-white"
                placeholder="Search products..."
              />
              <button 
                type="submit"
                className="absolute right-1 sm:right-1.5 top-1/2 -translate-y-1/2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-full p-1.5 sm:p-2.5 hover:from-primary-dark hover:to-primary transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                aria-label="Search"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </form>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Notifications - Desktop & Mobile */}
            {user && (
              <div className="relative">
                <button
                  ref={notificationsButtonRef}
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-secondary hover:text-primary transition-colors rounded-full hover:bg-gray-100"
                  aria-label="Notifications"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {notificationsOpen && typeof document !== 'undefined' && createPortal(
                  <>
                    <div 
                      className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                      onClick={() => setNotificationsOpen(false)}
                    />
                    <div 
                      className="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 w-[calc(100vw-2rem)] max-w-sm sm:w-80 md:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[9999] overflow-hidden animate-modal-pop max-h-[calc(100vh-120px)] sm:max-h-[80vh] flex flex-col"
                      style={(() => {
                        const buttonRect = notificationsButtonRef.current?.getBoundingClientRect();
                        const isMobile = window.innerWidth < 640;
                        const baseStyle = {
                          top: buttonRect ? `${Math.max(80, buttonRect.bottom + 8)}px` : '80px',
                        };
                        
                        if (!isMobile && buttonRect) {
                          baseStyle.right = `${window.innerWidth - buttonRect.right}px`;
                          baseStyle.left = 'auto';
                          baseStyle.transform = 'none';
                        }
                        
                        return baseStyle;
                      })()}
                    >
                      {/* Header */}
                      <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 py-3 flex items-center justify-between border-b border-white/20">
                        <h3 className="text-base font-bold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-white/90 hover:text-white font-medium underline"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>

                      {/* Notifications List */}
                      <div className="overflow-y-auto flex-1">
                        {loadingNotifications ? (
                          <div className="p-8 text-center">
                            <div className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <p className="mt-2 text-sm text-gray-500">Loading...</p>
                          </div>
                        ) : notifications.length === 0 ? (
                          <div className="p-8 text-center">
                            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            <p className="text-sm text-gray-500">No notifications</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${!notification.read_at ? 'bg-blue-50/50' : ''}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${!notification.read_at ? 'bg-primary' : 'bg-transparent'}`}></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{notification.message}</p>
                                    {notification.ticket_subject && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">Ticket: {notification.ticket_subject}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                      {new Date(notification.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
                          <button
                            onClick={() => {
                              const isAdmin = user?.role?.name === 'super_admin' || user?.role?.name === 'administrator' || user?.role?.name === 'manager';
                              navigate(isAdmin ? '/admin/support' : '/support');
                              setNotificationsOpen(false);
                            }}
                            className="text-xs text-primary hover:text-primary-dark font-medium w-full text-center py-2"
                          >
                            View All Support Tickets
                          </button>
                        </div>
                      )}
                    </div>
                  </>,
                  document.body
                )}
              </div>
            )}
            {/* Mobile Search - Expanded */}
            {mobileSearchOpen && (
              <>
                {/* Backdrop */}
                <div 
                  className="sm:hidden fixed inset-0 bg-black/20 z-[59]"
                  onClick={() => setMobileSearchOpen(false)}
                />
                {/* Search Container */}
                <form
                  ref={searchContainerRef}
                  className="sm:hidden fixed left-4 right-4 top-[73px] z-[60]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSearchSubmit(e);
                  }}
                >
                  <div className="relative">
                    <input 
                      type="text" 
                      value={localSearchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full pl-10 pr-12 py-2.5 border border-primary rounded-full focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary bg-white text-sm transition-all duration-200 shadow-lg"
                      placeholder="Search products..."
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMobileSearchOpen(false);
                        handleSearchChange('');
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Close search"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <button 
                      type="submit"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-full p-1.5 hover:from-primary-dark hover:to-primary transition-all duration-200 shadow-md"
                      aria-label="Search"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                </form>
              </>
            )}
            
            {/* Mobile Search Icon */}
            {!mobileSearchOpen && (
              <button 
                ref={searchIconRef}
                onClick={() => setMobileSearchOpen(true)}
                className="sm:hidden p-2 text-secondary hover:text-primary transition-colors"
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
            
            {/* Sign In/Sign Out - Mobile Icon */}
            {user ? (
              <div className="relative sm:hidden">
                <button
                  ref={mobileUserMenuButtonRef}
                  onClick={() => setMobileUserMenuOpen(!mobileUserMenuOpen)}
                  className="p-1 text-secondary hover:text-primary transition-colors rounded-full"
                  aria-label="User menu"
                >
                  <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-primary via-primary-dark to-secondary flex items-center justify-center text-white text-sm font-bold overflow-hidden border-2 border-gray-200">
                    {user?.profile_picture ? (
                      <img 
                        key={user.profile_picture}
                        src={user.profile_picture} 
                        alt={user?.name || 'User'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Mobile header avatar image failed to load:', user.profile_picture);
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          const fallback = parent.querySelector('.header-avatar-fallback');
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                        onLoad={() => {
                          console.log('Mobile header avatar image loaded successfully:', user.profile_picture);
                        }}
                      />
                    ) : null}
                    <span 
                      className="header-avatar-fallback absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary via-primary-dark to-secondary"
                      style={{ display: user?.profile_picture ? 'none' : 'flex' }}
                    >
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                </button>
                {mobileUserMenuOpen && typeof document !== 'undefined' && createPortal(
                  <>
                    <div 
                      className="fixed inset-0 z-[55]"
                      onClick={() => setMobileUserMenuOpen(false)}
                    />
                    <div 
                      className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[60]"
                      style={{
                        top: mobileUserMenuButtonRef.current ? `${mobileUserMenuButtonRef.current.getBoundingClientRect().bottom + 8}px` : 'auto',
                        right: mobileUserMenuButtonRef.current ? `${window.innerWidth - mobileUserMenuButtonRef.current.getBoundingClientRect().right}px` : '16px',
                      }}
                    >
                      <div className="py-2">
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setMobileUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-primary via-primary-dark to-secondary flex items-center justify-center text-white text-sm font-bold overflow-hidden border-2 border-gray-200 flex-shrink-0">
                            {user?.profile_picture ? (
                              <img 
                                key={user.profile_picture}
                                src={user.profile_picture} 
                                alt={user?.name || 'User'} 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.error('Mobile menu avatar image failed to load:', user.profile_picture);
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  const fallback = parent.querySelector('.mobile-menu-avatar-fallback');
                                  if (fallback) {
                                    fallback.style.display = 'flex';
                                  }
                                }}
                                onLoad={() => {
                                  console.log('Mobile menu avatar image loaded successfully:', user.profile_picture);
                                }}
                              />
                            ) : null}
                            <span 
                              className="mobile-menu-avatar-fallback absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary via-primary-dark to-secondary"
                              style={{ display: user?.profile_picture ? 'none' : 'flex' }}
                            >
                              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{user.name || 'User'}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          {user.role && (
                            <p className="text-xs text-primary mt-1">{user.role.display_name}</p>
                          )}
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setMobileUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Profile
                        </button>
                        <button
                          onClick={() => {
                            navigate('/support');
                            setMobileUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Support
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              navigate('/admin');
                              setMobileUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            Admin Panel
                          </button>
                        )}
                        {isCourier && (
                          <button
                            onClick={() => {
                              navigate('/admin/courier');
                              setMobileUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            Courier Dashboard
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (onLogout) {
                              onLogout();
                            }
                            setMobileUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="sm:hidden p-2 text-secondary hover:text-primary transition-colors"
                aria-label="Sign in"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            )}

            {/* Sign In/Sign Out - Desktop */}
            {user ? (
              <div className="relative hidden sm:flex">
                <button
                  ref={userMenuButtonRef}
                  onClick={() => setDesktopUserMenuOpen(true)}
                  className="text-secondary no-underline text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:text-primary hover:bg-primary/10 hover:font-bold transition-all duration-200 font-semibold flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
                >
                  <div className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary via-primary-dark to-secondary flex items-center justify-center text-white text-xs sm:text-sm font-bold overflow-hidden border-2 border-primary/30 flex-shrink-0">
                    {user?.profile_picture ? (
                      <img 
                        key={user.profile_picture}
                        src={user.profile_picture} 
                        alt={user?.name || 'User'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Desktop header avatar image failed to load:', user.profile_picture);
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          const fallback = parent.querySelector('.header-avatar-fallback-desktop');
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                        onLoad={() => {
                          console.log('Desktop header avatar image loaded successfully:', user.profile_picture);
                        }}
                      />
                    ) : null}
                    <span 
                      className="header-avatar-fallback-desktop absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary via-primary-dark to-secondary"
                      style={{ display: user?.profile_picture ? 'none' : 'flex' }}
                    >
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                  <span className="hidden md:inline">{user.name || user.email}</span>
                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* User Menu Modal - Rendered via Portal */}
                {desktopUserMenuOpen && typeof document !== 'undefined' && createPortal(
                  <>
                    <div 
                      className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                      onClick={() => setDesktopUserMenuOpen(false)}
                    />
                    <div 
                      className="fixed w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[9999] overflow-hidden animate-modal-pop"
                      style={{
                        top: userMenuButtonRef.current ? `${userMenuButtonRef.current.getBoundingClientRect().bottom + 8}px` : 'auto',
                        right: userMenuButtonRef.current ? `${window.innerWidth - userMenuButtonRef.current.getBoundingClientRect().right}px` : '16px',
                      }}
                    >
                      <div className="bg-gradient-to-r from-primary via-primary to-primary-dark px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              navigate('/profile');
                              setDesktopUserMenuOpen(false);
                            }}
                            className="flex-shrink-0 hover:opacity-90 transition-opacity"
                          >
                            <div className="relative w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-lg font-bold overflow-hidden border-2 border-white/30">
                              {user?.profile_picture ? (
                                <img 
                                  key={user.profile_picture}
                                  src={user.profile_picture} 
                                  alt={user?.name || 'User'} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    console.error('Desktop menu avatar image failed to load:', user.profile_picture);
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    const fallback = parent.querySelector('.header-menu-avatar-fallback');
                                    if (fallback) {
                                      fallback.style.display = 'flex';
                                    }
                                  }}
                                  onLoad={() => {
                                    console.log('Desktop menu avatar image loaded successfully:', user.profile_picture);
                                  }}
                                />
                              ) : null}
                              <span 
                                className="header-menu-avatar-fallback absolute inset-0 flex items-center justify-center bg-white/20"
                                style={{ display: user?.profile_picture ? 'none' : 'flex' }}
                              >
                                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                              </span>
                            </div>
                          </button>
                          <button
                            onClick={() => {
                              navigate('/profile');
                              setDesktopUserMenuOpen(false);
                            }}
                            className="flex-1 text-left hover:opacity-90 transition-opacity"
                          >
                            <p className="text-base font-bold text-white">{user.name || 'User'}</p>
                            <p className="text-xs text-white/80 mt-1">{user.email}</p>
                            {user.role && (
                              <span className="inline-block mt-2 text-xs font-bold text-white bg-white/20 px-2 py-1 rounded-md border border-white/30">
                                {user.role.display_name}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => setDesktopUserMenuOpen(false)}
                            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors ml-2"
                            aria-label="Close menu"
                          >
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="py-2">
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setDesktopUserMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-semibold">Profile</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/support');
                            setDesktopUserMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-semibold">Support</span>
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              navigate('/admin');
                              setDesktopUserMenuOpen(false);
                            }}
                            className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          >
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span className="font-semibold">Admin Panel</span>
                          </button>
                        )}
                        {isCourier && (
                          <button
                            onClick={() => {
                              navigate('/admin/courier');
                              setDesktopUserMenuOpen(false);
                            }}
                            className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
                          >
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            <span className="font-semibold">Courier Dashboard</span>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (onLogout) {
                              onLogout();
                            }
                            setDesktopUserMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span className="font-semibold">Logout</span>
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="text-secondary no-underline text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:text-primary hover:bg-primary/10 hover:font-bold transition-all duration-200 font-semibold hidden sm:flex items-center gap-1 sm:gap-1.5 whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="hidden md:inline">Sign in / Register</span>
                <span className="md:hidden">Sign in</span>
              </button>
            )}
            <Link to="/orders" className="text-secondary no-underline text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:text-primary hover:bg-primary/10 hover:font-bold transition-all duration-200 font-semibold hidden md:flex items-center gap-1 sm:gap-1.5 whitespace-nowrap">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Orders
            </Link>
            {user ? (
              <Link
                to="/support"
                className="text-secondary no-underline text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-md hover:text-primary hover:bg-primary/10 hover:font-bold transition-all duration-200 font-semibold hidden lg:flex items-center gap-1 sm:gap-1.5 whitespace-nowrap"
              >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Support
              </Link>
            ) : null}
            <button 
              ref={cartButtonRef}
              onClick={onCartClick}
              className="relative bg-primary text-white rounded-lg px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 hover:bg-primary-dark transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-1 sm:gap-2"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="hidden sm:inline text-xs sm:text-sm font-medium">Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[10px] sm:text-xs animate-pulse">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-secondary hover:text-primary transition-colors ml-1"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Modal */}
        <MobileMenuModal 
          isOpen={mobileMenuOpen} 
          onClose={() => setMobileMenuOpen(false)}
          activeFilter={activeFilter}
          onFilterChange={onFilterChange}
          onSearchClear={() => onSearchChange && onSearchChange('')}
          onCategoryFilterChange={onCategoryFilterChange}
        />

        {/* Auth Modal */}
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          onLoginSuccess={(user, token, options) => {
            if (onLoginSuccess) {
              onLoginSuccess(user, token, options);
            }
            setAuthModalOpen(false);
          }}
          redirectAfterLogin={redirectAfterLogin}
          redirectAfterRegister={redirectAfterRegister}
        />
      </div>
    </header>
  );
}

export default Header;
